import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { SPACE_DIRECTORY } from '@confluence/named-routes/entry-points/routes.ts';

import starredSpacesEyelashes from './assets/starred-spaces-eyelashes.svg';
import starredSpacesEyelashesDark from './assets/starred-spaces-eyelashes-dark.svg';
import starredSpacesTelescope from './assets/starred-spaces-telescope.svg';
import starredSpacesTelescopeDark from './assets/starred-spaces-telescope-dark.svg';

const SOURCE = 'starredSpacesEmptyState';

type StarredSpacesEmptyStateProps = {
	onExploreSpacesClick: () => void;
};

const styles = cssMap({
	emptyState: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		width: '100%',
	},
	emptyStateContent: {
		width: '100%',
	},
	emptyStateIcon: {
		alignItems: 'center',
		display: 'flex',
		flexShrink: 0,
		height: '32px',
		justifyContent: 'center',
		overflow: 'visible',
		position: 'relative',
		width: '32px',
	},
	emptyStateIconEyelashes: {
		height: '10px',
		position: 'absolute',
		right: token('space.negative.100'),
		top: token('space.negative.100'),
		transform: `translateY(${token('space.negative.025')}) rotate(15deg)`,
		width: '15px',
	},
	emptyStateText: {
		flexGrow: 1,
		minWidth: 0,
	},
	emptyStateAction: {
		width: '100%',
	},
});

const i18n = defineMessages({
	emptyStateExploreSpacesDescription: {
		id: 'side-navigation.starred-spaces-list.empty-state-explore-spaces-description',
		defaultMessage: 'Star spaces to keep them here for quick access.',
		description:
			'Description of the Mohiti empty state shown when there are user-created spaces but none have been starred.',
	},
	exploreSpaces: {
		id: 'side-navigation.starred-spaces-list.empty-state-explore-spaces',
		defaultMessage: 'Explore spaces',
		description: 'Primary action in the Mohiti empty state that links to the spaces directory.',
	},
});

export const StarredSpacesEmptyState = ({
	onExploreSpacesClick,
}: StarredSpacesEmptyStateProps): React.JSX.Element => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'starredSpacesEmptyStateScreen',
				source: SOURCE,
				attributes: {
					navVersion: '4',
				},
			},
		}).fire();
	}, [createAnalyticsEvent]);

	return (
		<Box paddingBlock="space.150" paddingInline="space.150" xcss={styles.emptyState}>
			<Stack alignInline="center" space="space.100">
				<Inline
					alignBlock="center"
					space="space.200"
					shouldWrap={false}
					xcss={styles.emptyStateContent}
				>
					<Box xcss={styles.emptyStateIcon}>
						<Image
							src={starredSpacesTelescope}
							srcDark={starredSpacesTelescopeDark}
							alt=""
							height="32px"
							width="32px"
						/>
						<Box xcss={styles.emptyStateIconEyelashes}>
							<Image
								src={starredSpacesEyelashes}
								srcDark={starredSpacesEyelashesDark}
								alt=""
								height="10px"
								width="15px"
							/>
						</Box>
					</Box>
					<Box xcss={styles.emptyStateText}>
						<Text color="color.text">
							<FormattedMessage {...i18n.emptyStateExploreSpacesDescription} />
						</Text>
					</Box>
				</Inline>
				<Box xcss={styles.emptyStateAction}>
					<LinkButton
						spacing="compact"
						shouldFitContainer
						href={SPACE_DIRECTORY.toUrl()}
						onClick={onExploreSpacesClick}
					>
						{intl.formatMessage(i18n.exploreSpaces)}
					</LinkButton>
				</Box>
			</Stack>
		</Box>
	);
};
